import geojson from "geojson";
import { rgb } from "d3-color";
import { Feature } from "geojson";
import { Coordinate } from "ol/coordinate";

import { api } from "./api";

import wheatIcon from "@assets/icons/crops/wheat.svg";
import cornIcon from "@assets/icons/crops/corn.svg";
import barleyIcon from "@assets/icons/crops/barley.svg";
import riceIcon from "@assets/icons/crops/rice.svg";
import oatsIcon from "@assets/icons/crops/oats.svg";
import ryeIcon from "@assets/icons/crops/rye.svg";
import soybeanIcon from "@assets/icons/crops/soybean.svg";
import peasIcon from "@assets/icons/crops/peas.svg";
import sunflowerIcon from "@assets/icons/crops/sunflower.svg";
import rapeseedIcon from "@assets/icons/crops/rapeseed.svg";
import potatoIcon from "@assets/icons/crops/potato.svg";
import sugarbeetIcon from "@assets/icons/crops/sugarbeet.svg";
import alfalfaIcon from "@assets/icons/crops/alfalfa.svg";
import grapesIcon from "@assets/icons/crops/grapes.svg";
import carrotIcon from "@assets/icons/crops/carrot.svg";
import beetIcon from "@assets/icons/crops/beet.svg";

const CROP_ICONS: Record<string, string> = {
  "crop-wheat": wheatIcon,
  "crop-corn": cornIcon,
  "crop-barley": barleyIcon,
  "crop-rice": riceIcon,
  "crop-oats": oatsIcon,
  "crop-rye": ryeIcon,
  "crop-soybean": soybeanIcon,
  "crop-peas": peasIcon,
  "crop-sunflower": sunflowerIcon,
  "crop-rapeseed": rapeseedIcon,
  "crop-potato": potatoIcon,
  "crop-sugarbeet": sugarbeetIcon,
  "crop-alfalfa": alfalfaIcon,
  "crop-grapes": grapesIcon,
  "crop-carrot": carrotIcon,
  "crop-beet": beetIcon,
};

// Fallback: match icon by crop name (case-insensitive)
const CROP_ICONS_BY_NAME: Record<string, string> = {
  wheat: wheatIcon,
  corn: cornIcon,
  barley: barleyIcon,
  rice: riceIcon,
  oats: oatsIcon,
  rye: ryeIcon,
  soybean: soybeanIcon,
  peas: peasIcon,
  sunflower: sunflowerIcon,
  rapeseed: rapeseedIcon,
  potato: potatoIcon,
  sugarbeet: sugarbeetIcon,
  "sugar beet": sugarbeetIcon,
  alfalfa: alfalfaIcon,
  grapes: grapesIcon,
  carrot: carrotIcon,
  beet: beetIcon,
  beetroot: beetIcon,
};

const getCropIcon = (id: string, name: string): string =>
  CROP_ICONS[id] ?? CROP_ICONS_BY_NAME[name.toLowerCase()] ?? "";

// Backend Response Interfaces
interface IFieldBackendResponse {
  id: string;
  name?: string;
  areaHectares?: number;
  color?: string;
  geometry?: geojson.Polygon;
  cropId?: string;
  cropName?: string;
  reductionPercent?: number;
  completed?: boolean;
  hasCameraData?: boolean;
  yieldEvened?: boolean;
  harvest?: TFieldHarvest;
  externalId?: string;
}

interface ICropBackendResponse {
  id: string;
  name: string;
  color: string;
  isBuiltin?: boolean;
  // True when the current user owns (and may edit/delete) this crop. Built-ins
  // (and any crop not owned by the user) are read-only.
  isOwn?: boolean;
}

interface ISeasonBackendResponse {
  id: string;
  start: string;
  end: string;
  isCurrent?: boolean;
}

export type TFieldGeometry = {
  type: string;
  coordinates: Array<Array<Array<Coordinate>>>;
};

export type TFieldStats = {
  min: number;
  max: number;
  avg: number;
  mass_kg: number;
};

export type TFieldHarvest = {
  harvestPercentage: number;
  harvestedAreaHectares: number;
  totalHexagons: number;
  filledHexagons: number;
  avgYield: number;
  totalMassKg: number;
};

export type TFieldProperties = {
  type: string;
  id: string;
  name: string;
  /**
   * Client-only: the real name while the avg-yield overview shows
   * "Name (12.3)" in `name`. Anything that edits or saves a field must read
   * `original_name ?? name`.
   */
  original_name?: string;
  season_id: string;
  culture_id: string;
  crop_name: string;
  /** Dirt/moisture percentage (0–100) deducted to show clean weight. */
  reduction_percent: number;
  /** User-confirmed "force complete" flag for this field + season (drives the green checkmark). */
  completed: boolean;
  /** True when potato-camera hex layers exist for this field + season. */
  has_camera_data: boolean;
  /**
   * True when the yield map is evened for this field + season. Applying an
   * evening also completes the field, so this implies `completed`.
   */
  yield_evened: boolean;
  fill: string;
  stroke: string;
  crop_fill: string;
  crop_stroke: string;
  area_m2: number;
  avg_yield: number;
  stats: TFieldStats | null;
  harvest: TFieldHarvest | null;
  externalId?: string;
};

export type TFieldResponse = {
  type: string;
  geometry: geojson.Polygon;
  properties: TFieldProperties;
};

export type TLegendItem = {
  start: number;
  end: number;
  range: string;
  percent: number;
  color: string;
};
export type TStatsItem = {
  avg: string;
  avg_value: number;
  total: string;
  total_value: number;
};

export type TFieldFeatures = {
  features: {
    type: "FeatureCollection";
    features: TFieldResponse[]
  };
};

// ── Yield evening ─────────────────────────────────────────
// Evens the yield map BY MACHINE: the backend reads the season's raw machine
// tracks, splits the field into areas harvested by one machine within one half
// hour and moves each area up or down as a whole so neighbouring machines
// agree. The pattern inside each area is kept and so is the field total. The
// tracks are read by a background scan, one per account and season (see
// prepareYieldEvening). The backend computes the preview and stores the
// applied result; the yield endpoint reports which map it returned in
// `evening`.

/** The earlier, pixel-only method's strengths. */
export type TLegacyEveningStrength = "light" | "medium" | "strong";

/**
 * What an applied evening was made with: "machine" is the current method;
 * light/medium/strong are the earlier pixel method, still found on fields
 * evened before it.
 */
export type TEveningStrength = "machine" | TLegacyEveningStrength;

/** What a by-machine evening changes (algorithmVersion 2). */
export type TEveningSummary = {
  algorithmVersion: number;
  strength: "machine";
  /** Hectares of yield cells considered. */
  areaHa: number;
  /** Display names of the machines compared. */
  machines: string[];
  /** Typical yield difference between neighbouring machine areas, as a fraction (0.07 = 7%). */
  stepBefore: number;
  /** The same after evening. */
  stepAfter: number;
  /** Fraction of the field area whose yield changed by 1% or more (0..1). */
  adjustedShare: number;
  adjustedAreaHa: number;
  /** Largest raise of an area, as a positive fraction (0.12 = +12%). */
  maxRaise: number;
  /** Largest cut of an area, as a positive fraction (0.10 = −10%). */
  maxCut: number;
  /** Centners/ha (same unit as stats.avg_value), dirt/moisture applied. */
  avgBefore: number;
  avgAfter: number;
  /** Kg, dirt/moisture applied. Evening keeps the field total. */
  totalMassKg: number;
};

/** What an evening made with the earlier pixel method changed. */
export type TLegacyEveningSummary = {
  algorithmVersion: number;
  strength: TLegacyEveningStrength;
  /** Hectares of yield cells considered. */
  areaHa: number;
  /** "Differences between areas" before evening, as a fraction (0.18 = 18%). */
  spreadBefore: number;
  /** The same after evening. */
  spreadAfter: number;
  /** Fraction of the area whose spikes/dips were replaced (0..1). */
  smoothedShare: number;
  smoothedAreaHa: number;
  /** Fraction of the area whose value changed by more than 5%. */
  movedShare: number;
  /** Largest typical raise, as a fraction (0.12 = +12%). */
  maxRaise: number;
  /** Largest typical cut, as a positive fraction (0.10 = −10%). */
  maxCut: number;
  /** Centners/ha (same unit as stats.avg_value), dirt/moisture applied. */
  avgBefore: number;
  avgAfter: number;
  /** Kg, dirt/moisture applied. Evening keeps the field total. */
  totalMassKg: number;
};

/** A summary of the current, by-machine method (as opposed to an earlier one). */
export const isMachineEveningSummary = (
  summary: TEveningSummary | TLegacyEveningSummary
): summary is TEveningSummary =>
  summary.strength === "machine" && Array.isArray((summary as TEveningSummary).machines);

/** Why the evening would change nothing. Apply is refused for these. */
export type TEveningNoop =
  | "no_tracks"
  | "single_machine"
  | "not_enough_data"
  | "nothing_to_even"
  | "too_large";

export type TEveningActive = {
  strength: TEveningStrength;
  appliedAt: string;
  summary: TEveningSummary | TLegacyEveningSummary;
};

export type TEveningPreview = {
  strength: "machine";
  /**
   * "pending": this season's machine tracks are not read yet (or went stale) —
   * call prepareYieldEvening again. The response's features are then the map
   * WITHOUT evening, not a preview.
   */
  status: "ready" | "pending";
  /** Identifies the yield data the preview was computed from; sent back on apply. "" unless ready. */
  fingerprint: string;
  /** Null when `noop` is set or the preview is pending. */
  summary: TEveningSummary | null;
  noop: TEveningNoop | null;
};

export type THeatmapEvening = {
  /** "stored" = what is saved (the active evening applied, if any),
   *  "original" = without the evening, "preview" = the by-machine evening. */
  view: "stored" | "original" | "preview";
  active: TEveningActive | null;
  /** Present only when view === "preview". */
  preview?: TEveningPreview;
};

/** The `evening` query param: "machine" previews the evening, "none" leaves it out. */
export type TEveningParam = "none" | "machine";

/** Where reading this season's machine tracks stands (see prepareYieldEvening). */
export type TEveningPrepare =
  /** The tracks for this field are read; the preview can be fetched. */
  | { status: "ready"; computedAt: string }
  /** Running or queued — poll again. `progress` is null until the scan knows its size. */
  | { status: "scanning"; startedAt: string; progress: { done: number; total: number } | null }
  /** busy = too many scans queued; scan_failed = the scan errored. Retry restarts it. */
  | { status: "failed"; error: "busy" | "scan_failed" }
  /** This server cannot read machine tracks at all. Nothing to retry. */
  | { status: "unavailable" };

export type THeatmapResponse = {
  features: geojson.FeatureCollection,
  legend: Array<TLegendItem>;
  stats: TStatsItem;
  /** Absent on old or empty responses — see heatmapEveningOf. */
  evening?: THeatmapEvening;
  /**
   * Client-only: the request failed (api.ts already toasted it). The rest is
   * an empty placeholder, not data — never render its zero stats as "0 t".
   */
  failed?: true;
};

/** A response's evening block. Missing means the stored map with nothing applied. */
export const heatmapEveningOf = (response: THeatmapResponse): THeatmapEvening =>
  response.evening ?? { view: "stored", active: null };

export type TSortBy = "name" | "size" | "harvestProgress" | "crop";
export type TSortOrder = "asc" | "desc";

/**
 * `withHarvest: false` asks the backend to leave out each field's progress,
 * for a caller that loads it from getHarvestProgress in parallel. A backend
 * that predates the switch ignores it and includes `harvest` anyway.
 */
export const getFieldsData = async (
  season_id?: string,
  sortBy?: TSortBy,
  sortOrder?: TSortOrder,
  farm_id?: string,
  withHarvest: boolean = true,
): Promise<TFieldFeatures> => {
  const body = await api.get<{ fields: IFieldBackendResponse[] }>("/api/v1/fields", {
    seasonId: season_id,
    farmId: farm_id,
    sortBy,
    sortOrder,
    harvest: withHarvest ? undefined : "false",
  });

  const fields = body.fields ?? [];

  const features: TFieldResponse[] = fields.map((field) => {
    const areaM2 = (field.areaHectares ?? 0) * 10000;
    const fieldColor = field.color ?? "#4CAF50";
    const color = rgb(fieldColor);
    color.opacity = 0.6;

    return {
      type: "Feature",
      geometry: field.geometry ?? { type: "Polygon", coordinates: [] },
      properties: {
        type: "field",
        id: field.id,
        name: field.name ?? "",
        season_id: "",
        culture_id: field.cropId ?? "",
        crop_name: field.cropName ?? "",
        reduction_percent: field.reductionPercent ?? 0,
        completed: field.completed ?? false,
        has_camera_data: field.hasCameraData ?? false,
        yield_evened: field.yieldEvened ?? false,
        fill: color.toString(),
        stroke: fieldColor,
        crop_fill: color.toString(),
        crop_stroke: fieldColor,
        area_m2: areaM2,
        avg_yield: field.harvest?.avgYield ?? 0,
        stats: null,
        harvest: field.harvest ?? null,
        externalId: field.externalId,
      },
    };
  });

  return {
    features: {
      type: "FeatureCollection",
      features,
    },
  };
};

/** Progress of a field with no yield map this season. */
export const NO_HARVEST: Readonly<TFieldHarvest> = Object.freeze({
  harvestPercentage: 0,
  harvestedAreaHectares: 0,
  totalHexagons: 0,
  filledHexagons: 0,
  avgYield: 0,
  totalMassKg: 0,
});

/**
 * Harvest progress per field id for the season (and farm), loaded beside the
 * field list so the list and map never wait for it. Fields with no yield map
 * are absent: their progress is NO_HARVEST. Silent: the list stays usable
 * without it, and an older backend answers this path with 404.
 */
export const getHarvestProgress = async (
  season_id?: string,
  farm_id?: string,
): Promise<Map<string, TFieldHarvest>> => {
  const body = await api.get<{ harvest: Record<string, TFieldHarvest> }>(
    "/api/v1/fields/harvest-progress",
    { seasonId: season_id, farmId: farm_id },
    { silent: true },
  );
  return new Map(Object.entries(body.harvest ?? {}));
};

export type TFieldListItem = { id: string; name: string; externalId?: string };

export const getFieldsList = async (
  season_id?: string
): Promise<TFieldListItem[]> => {
  // Names only: no boundaries, no progress.
  const body = await api.get<{ fields: IFieldBackendResponse[] }>(
    "/api/v1/fields",
    { seasonId: season_id, compact: "true", harvest: "false" }
  );
  const fields = body.fields ?? [];
  return fields.map((f) => ({
    id: f.id,
    name: f.name ?? "",
    externalId: f.externalId,
  }));
};

export type TYieldScale = "quantize" | "quantile";

/**
 * The yield heatmap. Without `evening` it is the stored map (any applied
 * evening included); `evening` asks for the map without it ("none") or for the
 * by-machine preview ("machine"). `stats` always describe the returned map.
 */
export const getHeatmapData = async (
  id: string,
  season_id?: string,
  scale?: TYieldScale,
  evening?: TEveningParam
): Promise<THeatmapResponse> => {
  try {
    return await api.get<THeatmapResponse>(`/api/v1/fields/${id}/yield`, { seasonId: season_id, scale, evening });
  } catch (e) {
    return {
      stats: { avg: "0", avg_value: 0, total: "0", total_value: 0 },
      features: { type: "FeatureCollection", features: [] },
      legend: [],
      failed: true,
    };
  }
};

// ── Machine tracks on a field ───────────────────────────────

export type TFieldTrackDay = {
  day: string;
  deviceId: string;
  attachmentId: string | null;
  /** Machine display name. Present on the summary so the picker can be
   *  labelled before any points are fetched. */
  name: string;
  points: number;
  /** First/last fix of this UTC day's row (ISO) — what places it on the
   *  viewer's local days. Absent from backends older than 2026-10-08. */
  firstAt?: string;
  lastAt?: string;
};

/** One machine's points for the requested days. `tracks` is the same row shape
 *  as GET /devices/:id/tracks, so DeviceStatisticsResponse parses it as-is. */
export type TFieldTrackDevice = {
  attachmentId: string;
  deviceId: string;
  name: string;
  /** Reaper width in metres — the swath the ribbon is drawn at. */
  width: number;
  /** Sensor-to-GPS delay in ms, from the device's own settings. */
  delay: number;
  /** Fewest satellites a fix must report to be drawn (0 = no filter), from the
   *  device's own settings. */
  minSatellitesCount: number;
  /** Admin working-speed band, km/h. Optional: an older backend omits it. */
  minSpeed?: number;
  maxSpeed?: number;
  /** Analytics turn gate (min_turn_radius_m), metres; 0 = off. Optional: an
   *  older backend omits it and the default applies. */
  minTurnRadiusM?: number;
  tracks: any[];
};

export type TFieldTracksResponse = {
  days: TFieldTrackDay[];
  devices: TFieldTrackDevice[];
  computedThrough: string | null;
  truncated: boolean;
  /** A season scan is still running — `days` may grow. Show progress and poll
   *  rather than telling the user there are no tracks. */
  refreshing: boolean;
  /** Client-only: the request failed (already toasted). Empty placeholder —
   *  never cache it as "no tracks on these days" or apply it over real days. */
  failed?: true;
};

const EMPTY_TRACKS: TFieldTracksResponse = {
  days: [],
  devices: [],
  computedThrough: null,
  truncated: false,
  refreshing: false,
};

/**
 * Tracks for one field across a season.
 *
 * Called twice per field: once with no `days` for the cheap day summary that
 * fills the day picker, then once per batch of days the user actually wants
 * drawn. Asking for a whole season of points in one call would be a
 * double-digit-megabyte response.
 */
export const getFieldTracks = async (
  id: string,
  season_id: string,
  days?: string[],
  deviceIds?: string[]
): Promise<TFieldTracksResponse> => {
  try {
    return await api.get<TFieldTracksResponse>(`/api/v1/fields/${id}/tracks`, {
      seasonId: season_id,
      days: days?.length ? days.join(",") : undefined,
      deviceIds: deviceIds?.length ? deviceIds.join(",") : undefined,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  } catch (e) {
    console.warn(e);
    return { ...EMPTY_TRACKS, failed: true };
  }
};

/**
 * Download URL for a field's raw season points as CSV, SHP (zip) or KML.
 *
 * The filter params are optional — omitted means "every machine, every point
 * that passes the device settings". Speeds are km/h; hidden ranges are on the
 * 0–100 sensor scale the preview colors with, `lo` inclusive, `hi` exclusive.
 */
export const fieldTrackPointsExportUrl = (
  id: string,
  opts: {
    seasonId: string;
    format: "csv" | "shp" | "kml";
    fieldName: string | null;
    units: { weight: string; area: string; poundsPerBushel: number; poundsPerBag: number };
    deviceIds?: string[];
    hiddenRanges?: ReadonlyArray<{ lo: number; hi: number }>;
    speedMinKmh?: number | null;
    speedMaxKmh?: number | null;
    removeTurns?: boolean;
  }
): string => {
  const base = process.env.REACT_APP_API_URL ?? window.location.origin;
  const q = new URLSearchParams();
  q.append("seasonId", opts.seasonId);
  q.append("format", opts.format);
  if (opts.fieldName) q.append("field_name", opts.fieldName.replaceAll(" ", "_"));
  q.append("weight", opts.units.weight);
  q.append("area", opts.units.area);
  q.append("poundsPerBushel", String(opts.units.poundsPerBushel));
  q.append("poundsPerBag", String(opts.units.poundsPerBag));
  if (opts.deviceIds) q.append("deviceIds", opts.deviceIds.join(","));
  if (opts.hiddenRanges?.length) {
    q.append("hiddenRanges", opts.hiddenRanges.map((r) => `${r.lo}-${r.hi}`).join(","));
  }
  if (opts.speedMinKmh != null) q.append("speedMin", String(opts.speedMinKmh));
  if (opts.speedMaxKmh != null) q.append("speedMax", String(opts.speedMaxKmh));
  if (opts.removeTurns) q.append("removeTurns", "1");
  return `${base}/api/v1/fields/${id}/tracks/export?${q}`;
};

// ── Potato-camera layers ────────────────────────────────────

export type TCameraMetric = "count" | "stone_percent" | "mean_size";

export type TCameraMeta = {
  bucket_edges: number[];
  size_unit: "px" | "mm";
  mm_per_px: number | null;
} | null;

export type TCameraStats = {
  potato_total: number;
  stone_total: number;
  stone_percent: number;
  mean_size: number | null;
  hex_count: number;
  histogram: number[] | null;
  meta: TCameraMeta;
} | null;

export type TCameraHeatmapResponse = {
  features: geojson.FeatureCollection;
  legend: Array<TLegendItem>;
  stats: TCameraStats;
};

export type TCameraDailyEntry = {
  date: string;
  potato_count: number;
  stone_count: number;
  stone_percent: number;
  mean_size: number | null;
  histogram: number[] | null;
};

export const getCameraHeatmapData = async (
  id: string,
  season_id: string | undefined,
  metric: TCameraMetric,
  scale?: TYieldScale
): Promise<TCameraHeatmapResponse> => {
  try {
    return await api.get<TCameraHeatmapResponse>(`/api/v1/fields/${id}/camera`, {
      seasonId: season_id,
      metric,
      scale,
    });
  } catch (e) {
    return {
      features: { type: "FeatureCollection", features: [] },
      legend: [],
      stats: null,
    };
  }
};

export const getCameraDailyData = async (
  id: string,
  season_id: string
): Promise<{ days: TCameraDailyEntry[]; meta: TCameraMeta }> => {
  try {
    return await api.get<{ days: TCameraDailyEntry[]; meta: TCameraMeta }>(
      `/api/v1/fields/${id}/camera/daily`,
      { seasonId: season_id }
    );
  } catch (e) {
    return { days: [], meta: null };
  }
};

// ── Multi-layer prescription sources ────────────────────────

/** One selectable input layer for a prescription map. */
export type TPrescriptionLayerSpec =
  | { type: "yield"; seasonId: string }
  | { type: "productivity" };

export type TPrescriptionSurface = "union" | "intersection";

export type TPrescriptionYieldSeason = {
  seasonId: string;
  seasonName: string;
  isCurrent: boolean;
  filledHexagons: number;
  avgYield: number;
};

export type TPrescriptionSources = {
  currentSeasonId: string | null;
  yieldSeasons: TPrescriptionYieldSeason[];
  productivity:
    | { available: true; zoneCount: number; resolution: number; yearsUsed: number[]; seasonsValid: number }
    | { available: false };
};

/** List the yield seasons + productivity map available to seed a prescription. */
export const getPrescriptionSources = async (fieldId: string): Promise<TPrescriptionSources> =>
  api.get<TPrescriptionSources>(`/api/v1/fields/${fieldId}/prescription/sources`);

export interface PrescriptionUploadToFilesRequest {
  fieldId: string;
  seasonId?: string;
  // Multi-layer mode: when provided, takes precedence over seasonId on the backend.
  layers?: TPrescriptionLayerSpec[];
  surface?: TPrescriptionSurface;
  zones: number;
  rates: number[];
  fileName?: string;
  deereOrgId?: string;
}

export interface PrescriptionUploadToFilesResponse {
  id: string;
  name: string;
  size: number;
  contentType: string;
}

export const uploadPrescriptionToFiles = (
  fieldId: string,
  body: Omit<PrescriptionUploadToFilesRequest, "fieldId">
): Promise<PrescriptionUploadToFilesResponse> =>
  api.post<PrescriptionUploadToFilesResponse>(
    `/api/v1/fields/${fieldId}/prescription/upload-to-files`,
    body
  );

// ── John Deere yield-map sync (Map Layers API) ──────────────

export interface YieldSyncToDeereRequest {
  seasonId: string;
  title?: string;
  text?: string;
  deereOrgId?: string;
  replaceExisting?: boolean;
  weight?: string;
  area?: string;
  poundsPerBushel?: number;
  poundsPerBag?: number;
}

export interface YieldSyncLegendBin {
  label: string;
  minimum: number;
  maximum: number;
  hexColor: string;
  percent: number;
}

/**
 * John Deere Map Layer image processing status. Per the Map Layer Event docs:
 *   - QUEUED          — initial state for any Map Layer that received an image
 *   - VALID           — image processed and associated with the Map Layer
 *   - INVALID         — image was unsuccessfully processed
 *   - NO_FILE_RESOURCE — image could not be found as a file resource
 */
export type YieldSyncDeereStatus =
  | "QUEUED"
  | "VALID"
  | "INVALID"
  | "NO_FILE_RESOURCE";

export interface YieldSyncToDeereResponse {
  status: YieldSyncDeereStatus;
  summaryUri: string;
  mapLayerUri: string;
  fileResourceUri: string;
  legend: YieldSyncLegendBin[];
  unit: string;
  pointCount: number;
}

export type YieldSyncStatus = {
  status: YieldSyncDeereStatus | null;
  mapLayerUri?: string;
  summaryUri?: string;
  syncedAt?: string;
  error?: string;
  pollError?: string;
};

export const syncYieldToDeere = (
  fieldId: string,
  body: YieldSyncToDeereRequest
): Promise<YieldSyncToDeereResponse> =>
  api.post<YieldSyncToDeereResponse>(
    `/api/v1/fields/${fieldId}/yield/sync-to-deere`,
    body
  );

export const getYieldDeereSyncStatus = (
  fieldId: string,
  seasonId: string
): Promise<YieldSyncStatus> =>
  api.get<YieldSyncStatus>(
    `/api/v1/fields/${fieldId}/yield/sync-to-deere/status`,
    { seasonId }
  );

export interface BulkYieldSyncResult {
  seasonId: string;
  total: number;
  /** Count per status, e.g. { synced: 2, "not-linked": 1 } */
  summary: Record<string, number>;
  results: Array<{ fieldId: string; status: string; error?: string }>;
}

/** Push yield maps for multiple selected fields to John Deere in one request. */
export const bulkSyncYieldToDeere = (
  fieldIds: (string | number)[],
  seasonId?: string
): Promise<BulkYieldSyncResult> =>
  api.post<BulkYieldSyncResult>("/api/v1/fields/yield/sync-to-deere/bulk", {
    fieldIds: fieldIds.map(String),
    seasonId: seasonId || undefined,
  });

// ── NDVI ────────────────────────────────────────────────────

export type TNdviDateItem = {
  date: string;
  cloudCover: number;
};

export type TNdviDatesResponse = {
  dates: TNdviDateItem[];
  total: number;
  offset: number;
  limit: number;
};

const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;

/**
 * Dates fetched per page of the spectral strip.
 *
 * This is the real cap on what opening a field costs: every card in the strip is
 * one Sentinel Hub request against a capped monthly quota, and on a desktop-width
 * strip roughly fourteen cards sit in view at once — so the lazy loading in
 * useInView barely narrows a page of 20, and opening a field spent all 20 before
 * the user had shown interest in any single date. Eight fills the visible strip on
 * a phone and most of one on a desktop; older dates arrive via "More".
 */
export const NDVI_DATE_PAGE_SIZE = 8;

export const getNdviDates = async (
  fieldId: string,
  limit = NDVI_DATE_PAGE_SIZE,
  offset = 0
): Promise<TNdviDatesResponse> => {
  return api.get<TNdviDatesResponse>(`/api/v1/fields/${fieldId}/ndvi/dates`, { limit, offset });
};

// Spectral layers share the same Sentinel-2 scenes/dates; only the backend
// URL segment (and thus the color-mapped image) differs.
export type TSpectralLayer = "ndvi" | "brightness" | "moisture";

const SPECTRAL_SEGMENT: Record<TSpectralLayer, string> = {
  ndvi: "ndvi",
  brightness: "soil-brightness",
  moisture: "soil-moisture",
};

/** Returns an object URL for a spectral-layer image (caller must revoke when done) */
export const getSpectralImageUrl = async (
  fieldId: string,
  date: string,
  layer: TSpectralLayer,
  thumbnail = false
): Promise<string> => {
  const endpoint = thumbnail ? "thumbnail" : "image";
  const segment = SPECTRAL_SEGMENT[layer];
  const url = `${BASE_URL}/api/v1/fields/${fieldId}/${segment}/${endpoint}?date=${date}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`${layer} ${endpoint} fetch failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

/** Returns an object URL for the NDVI image (caller must revoke when done) */
export const getNdviImageUrl = async (
  fieldId: string,
  date: string,
  thumbnail = false
): Promise<string> => {
  return getSpectralImageUrl(fieldId, date, "ndvi", thumbnail);
};

// ── Productivity Map (multi-year NDVI management zones) ──
export type TProductivityLegendItem = {
  zone: number;
  label: string;
  color: string;
  hexCount: number;
};

export type TProductivityZoneStat = {
  zone: number;
  label: string;
  relativeProductivity: number;
  areaHectares: number;
  percent: number;
};

export type TProductivityStats = {
  seasonsValid: number;
  yearsUsed: number[];
  hexCount: number;
  areaHectares: number;
  zones: TProductivityZoneStat[];
};

export type TProductivityMap = {
  geojson?: { type: "FeatureCollection"; features: any[] };
  legend?: TProductivityLegendItem[];
  stats?: TProductivityStats;
  zoneCount?: number;
  yearsUsed?: number[];
  seasonsValid?: number;
  generatedAt?: string;
  exists?: boolean; // false when no stored map exists yet
};

export type TProductivityJob = {
  status: "none" | "pending" | "running" | "complete" | "failed";
  jobId?: string;
  progress?: number;
  stage?: string | null;
  error?: string | null;
};

/** Stored productivity map for a field, or `{ exists: false }`.
 * `cache: "no-store"` so the refetch right after generation never gets a stale
 * `{exists:false}` from the browser HTTP cache (which would leave the UI stuck
 * on the empty-state button until a manual reload). */
export const getProductivityMap = async (fieldId: string): Promise<TProductivityMap> =>
  api.get<TProductivityMap>(`/api/v1/fields/${fieldId}/productivity`, undefined, {
    cache: "no-store",
  });

/** Kick off (or reuse) generation of the multi-year productivity map.
 * Pass force=true to discard a stored map and rebuild it. */
export const startProductivityGeneration = async (
  fieldId: string,
  force = false
): Promise<TProductivityJob> =>
  api.post<TProductivityJob>(
    `/api/v1/fields/${fieldId}/productivity/generate${force ? "?force=true" : ""}`,
    {}
  );

/** Poll generation job status. */
export const getProductivityStatus = async (fieldId: string): Promise<TProductivityJob> =>
  api.get<TProductivityJob>(`/api/v1/fields/${fieldId}/productivity/status`);

/** Direct download URL for the productivity export (cookie-authenticated GET). */
export const getProductivityExportUrl = (fieldId: string, format: "shp" | "kml"): string =>
  `${BASE_URL}/api/v1/fields/${fieldId}/productivity/export?format=${format}`;

export type TProductivityDeereStatus = "QUEUED" | "VALID" | "INVALID" | "NO_FILE_RESOURCE" | "FAILED";

export type TProductivitySyncStatus = {
  status: TProductivityDeereStatus | null;
  mapLayerUri?: string;
  summaryUri?: string;
  syncedAt?: string;
  error?: string;
  pollError?: string;
};

/** Push the productivity map to John Deere as a Map Layer (distinct from yield). */
export const syncProductivityToDeere = (
  fieldId: string,
  body: { replaceExisting?: boolean } = {}
): Promise<{ status: TProductivityDeereStatus; mapLayerUri: string }> =>
  api.post(`/api/v1/fields/${fieldId}/productivity/sync-to-deere`, body);

export const getProductivityDeereSyncStatus = (
  fieldId: string
): Promise<TProductivitySyncStatus> =>
  api.get<TProductivitySyncStatus>(`/api/v1/fields/${fieldId}/productivity/sync-to-deere/status`);

export const updateField = async (feature: Feature, farm_id?: string, season_id?: string) => {
  if (feature.properties!["id"]) {
    await api.patch(
      `/api/v1/fields/${feature.properties!["id"]}`,
      {
        name: feature.properties!["name"],
        geometry: feature.geometry,
        color: feature.properties!["stroke"] ?? feature.properties!["fill"],
        cropId: feature.properties!["culture_id"] || undefined,
        farmId: farm_id || undefined,
        // Per-season force-complete flag. Sent with seasonId so the backend
        // upserts the right field_cultures row (mirrors updateFieldReduction).
        completed: feature.properties!["completed"],
        seasonId: season_id || undefined,
      },
      // The caller toasts: it translates 409 YIELD_EVENED (un-completing an
      // evened field) and shows the server message for anything else.
      { silent: true }
    );
  } else {
    // Pass both through — dropping them here filed the new field under the
    // account's fallback farm and no season, instead of the farm the user is
    // actually looking at. Note the argument order differs from this function's:
    // createField takes (feature, season, farm).
    await createField(feature, season_id, farm_id);
  }
};

export const createField = async (feature: Feature, season_id?: string, farm_id?: string) => {
  await api.post(
    "/api/v1/fields",
    {
      name: feature.properties!["name"],
      geometry: feature.geometry,
      color: feature.properties!["stroke"] ?? feature.properties!["fill"],
      cropId: feature.properties!["culture_id"] || undefined,
      seasonId: season_id || undefined,
      farmId: farm_id || undefined,
    },
    // The caller toasts (FieldsScreenVM / saveField's callers).
    { silent: true }
  );
};

/**
 * Update only a field's dirt/moisture % (clean-weight reduction) for a season.
 * Negative values are allowed and raise the yield instead of lowering it —
 * the same control corrects a scale that under-reports.
 * The backend invalidates its yield cache so the next heatmap/stats read
 * reflects the new clean weight.
 */
export const updateFieldReduction = async (
  fieldId: string,
  reductionPercent: number,
  seasonId?: string,
): Promise<void> => {
  await api.patch(
    `/api/v1/fields/${fieldId}`,
    {
      reductionPercent: Math.min(100, Math.max(-100, reductionPercent)),
      seasonId: seasonId || undefined,
    },
    // FieldsScreenVM.updateFieldReduction toasts the failure.
    { silent: true }
  );
};

export const deleteField = async (id: string | number) => {
  await api.delete(`/api/v1/fields/${id}`);
};

export const deleteAllFields = async () => {
  await api.delete("/api/v1/fields");
};

// ── Bulk field operations ──

export const bulkChangeCrop = async (
  fieldIds: (string | number)[],
  cropId: string,
  seasonId?: string
): Promise<{ updated: number }> => {
  return api.post("/api/v1/fields/bulk/change-crop", {
    fieldIds: fieldIds.map(String),
    cropId,
    seasonId: seasonId || undefined,
  });
};

export const bulkSetCompleted = async (
  fieldIds: (string | number)[],
  completed: boolean,
  seasonId?: string
): Promise<{ updated: number }> => {
  return api.post("/api/v1/fields/bulk/complete", {
    fieldIds: fieldIds.map(String),
    completed,
    seasonId: seasonId || undefined,
  });
};

export const bulkDeleteFields = async (
  fieldIds: (string | number)[]
): Promise<{ deleted: number }> => {
  return api.post("/api/v1/fields/bulk/delete", {
    fieldIds: fieldIds.map(String),
  });
};

/**
 * Bulk-export selected field boundaries to a single KML or shapefile zip,
 * then trigger a browser download. Uses a raw fetch because the response is
 * binary (the shared api client only parses JSON).
 */
export const bulkExportFields = async (
  fieldIds: (string | number)[],
  format: "kml" | "shp",
  seasonId?: string
): Promise<void> => {
  const res = await fetch(`${BASE_URL}/api/v1/fields/bulk/export`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fieldIds: fieldIds.map(String),
      format,
      seasonId: seasonId || undefined,
    }),
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = format === "kml" ? "fields.kml" : "fields.shp.zip";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export type TCategory = {
  color: string;
  icon: { url: string };
  id: string;
  name: string;
};

export type TCrop = {
  category: TCategory;
  id: string;
  is_builtin: boolean;
  // Whether the current user can edit/delete this crop (false for built-ins).
  is_own: boolean;
  name: string;
};

export const getCropsData = async (): Promise<TCrop[]> => {
  const body = await api.get<{ crops: ICropBackendResponse[] }>("/api/v1/crops");
  const crops = body.crops ?? [];

  return crops.map((c) => ({
    id: c.id,
    name: c.name,
    is_builtin: c.isBuiltin ?? true,
    // Fall back to !isBuiltin for older backends that don't send isOwn.
    is_own: c.isOwn ?? !(c.isBuiltin ?? true),
    category: {
      id: c.id,
      name: c.name,
      color: c.color,
      icon: { url: getCropIcon(c.id, c.name) },
    },
  }));
};

export const getCategories = async (): Promise<TCategory[]> => {
  // Categories are derived from crops — no separate endpoint needed.
  const crops = await getCropsData();
  return crops.map((c) => c.category);
};

export type TCreateCropRequest = {
  name: string;
  category_id: string;
};

export const createCrop = async (culture: TCreateCropRequest) => {
  // category_id points to an existing crop whose color we reuse.
  const allCrops = await getCropsData();
  const parent = allCrops.find((c) => c.id === culture.category_id);
  const color = parent?.category.color ?? "#888888";

  await api.post("/api/v1/crops", { name: culture.name, color });
};

export const removeCrop = async (id: string) => {
  await api.delete(`/api/v1/crops/${id}`);
};

export const editCrop = async (id: string, culture: TCreateCropRequest) => {
  const allCrops = await getCropsData();
  const parent = allCrops.find((c) => c.id === culture.category_id);
  const color = parent?.category.color ?? "#888888";

  await api.patch(`/api/v1/crops/${id}`, { name: culture.name, color });
};

export interface Season {
  // Season id
  id: string;
  // Season start date
  from: Date,
  // Season end date
  to: Date;
  // Is season default?
  default: boolean;
};

export const listSeasons = async (): Promise<Season[]> => {
  const body = await api.get<{ seasons: ISeasonBackendResponse[] }>("/api/v1/seasons");
  const seasons = body.seasons ?? [];

  return seasons.map((s) => ({
    id: s.id,
    from: new Date(s.start),
    to: new Date(s.end),
    default: s.isCurrent ?? false,
  }));
};

export const currentSeason = async (): Promise<Season> => {
  const seasons = await listSeasons();
  const current = seasons.find((s) => s.default);
  if (current) return current;
  if (seasons.length > 0) return seasons[0];
  throw new Error("No seasons found");
};

// ── Yield correction zones ────────────────────────────────
// Areas of a field whose measured yield is a technical artifact (sensor
// spikes along combine passes). The backend stores the geometry and applies
// the correction on read, so it survives the ~2-hourly yield re-import.

export type TYieldAdjustmentMode = "percent" | "field_average";

export type TYieldAdjustment = {
  id: string;
  fieldId: string;
  seasonId: string;
  name: string | null;
  mode: TYieldAdjustmentMode;
  /** −100–100; negative raises the yield. Null when mode is "field_average". */
  reductionPercent: number | null;
  geometry: geojson.MultiPolygon;
  areaHectares: number;
};

export const listYieldAdjustments = async (
  fieldId: string,
  seasonId: string
): Promise<TYieldAdjustment[]> => {
  const res = await api.get<{ adjustments: TYieldAdjustment[] }>(
    `/api/v1/fields/${fieldId}/yield/adjustments`,
    { seasonId }
  );
  return res.adjustments;
};

export const createYieldAdjustment = async (
  fieldId: string,
  data: {
    seasonId: string;
    name?: string | null;
    mode: TYieldAdjustmentMode;
    reductionPercent?: number | null;
    geometry: geojson.MultiPolygon;
  }
): Promise<TYieldAdjustment> => {
  // Silent: the VM toasts, translating 409 YIELD_EVENED (zones are locked
  // while the yield is evened).
  return api.post<TYieldAdjustment>(
    `/api/v1/fields/${fieldId}/yield/adjustments`,
    data,
    { silent: true }
  );
};

export const updateYieldAdjustment = async (
  fieldId: string,
  adjustmentId: string,
  data: Partial<{
    name: string | null;
    mode: TYieldAdjustmentMode;
    reductionPercent: number | null;
    geometry: geojson.MultiPolygon;
  }>
): Promise<TYieldAdjustment> => {
  return api.patch<TYieldAdjustment>(
    `/api/v1/fields/${fieldId}/yield/adjustments/${adjustmentId}`,
    data,
    { silent: true }
  );
};

export const deleteYieldAdjustment = async (
  fieldId: string,
  adjustmentId: string
): Promise<void> => {
  await api.delete(`/api/v1/fields/${fieldId}/yield/adjustments/${adjustmentId}`, undefined, {
    silent: true,
  });
};

// ── Yield evening (prepare / apply / remove) ──────────────
// All three calls are silent: the VM turns failures (and the error codes
// below) into the panel's own notices or one translated toast each.

/** The backend's machine-readable reason for a failed call (see api.ts). */
export const apiErrorCode = (e: unknown): string | undefined =>
  (e as { errorCode?: string } | null)?.errorCode;

/**
 * 409 YIELD_CHANGED — the yield data changed since the preview (prepare and
 * reload it);
 * 409 YIELD_BUSY — the map is being recomputed (try again shortly);
 * 400 YIELD_EVEN_NOOP — the evening would change nothing;
 * 409 YIELD_EVENED — a change the evening locks (un-completing the field,
 * editing correction zones).
 */
export type TYieldEveningErrorCode =
  | "YIELD_CHANGED"
  | "YIELD_BUSY"
  | "YIELD_EVEN_NOOP"
  | "YIELD_EVENED";

/**
 * Have this season's machine tracks read for the by-machine evening, or ask
 * how far that is. One background scan per account and season: the first
 * field of a season takes a few minutes, every other field of it is then
 * instant. Poll while it answers `scanning`; `retry` restarts a failed scan.
 * Needs edit rights.
 */
export const prepareYieldEvening = (
  fieldId: string,
  seasonId: string,
  retry = false
): Promise<TEveningPrepare> =>
  api.post<TEveningPrepare>(
    `/api/v1/fields/${fieldId}/yield/evening/prepare`,
    retry ? { seasonId, retry: true } : { seasonId },
    { silent: true }
  );

/**
 * Apply the previewed by-machine evening. Also completes the field for the
 * season and freezes it. Replaces an evening made with the earlier method.
 */
export const applyYieldEvening = (
  fieldId: string,
  seasonId: string,
  fingerprint: string
): Promise<{ evening: TEveningActive }> =>
  api.post<{ evening: TEveningActive }>(
    `/api/v1/fields/${fieldId}/yield/evening`,
    { seasonId, fingerprint },
    { silent: true }
  );

/** Restore the measured map. The field stays completed. */
export const removeYieldEvening = (
  fieldId: string,
  seasonId: string
): Promise<{ success: boolean }> =>
  api.post<{ success: boolean }>(
    `/api/v1/fields/${fieldId}/yield/evening/remove`,
    { seasonId },
    { silent: true }
  );

// ── Field Trials ──────────────────────────────────────────

export type TTrialShape = "line_width" | "polygon";

export type TFieldTrial = {
  id: string;
  fieldId: string;
  seasonId: string;
  name: string;
  shape: TTrialShape;
  // Center-line + width — present only for line_width trials; null for polygon trials.
  pointA: { lng: number; lat: number } | null;
  pointB: { lng: number; lat: number } | null;
  widthMeters: number | null;
  geometry: geojson.Polygon;
  areaHectares: number;
};

export type TTrialYield = {
  totalMass: number;
  avgYield: number;
  hexCount: number;
  totalHexes: number;
};

export type TTrialWithYield = TFieldTrial & { yield: TTrialYield };

export const listTrials = async (
  fieldId: string,
  seasonId?: string
): Promise<TFieldTrial[]> => {
  const res = await api.get<{ trials: TFieldTrial[] }>(
    `/api/v1/fields/${fieldId}/trials`,
    { seasonId }
  );
  return res.trials;
};

export type TCreateTrialData =
  | {
      shape?: "line_width";
      name: string;
      seasonId: string;
      pointA: { lng: number; lat: number };
      pointB: { lng: number; lat: number };
      widthMeters: number;
    }
  | {
      shape: "polygon";
      name: string;
      seasonId: string;
      geometry: geojson.Polygon;
    };

export const createTrial = async (
  fieldId: string,
  data: TCreateTrialData
): Promise<TFieldTrial> => {
  return api.post<TFieldTrial>(`/api/v1/fields/${fieldId}/trials`, data);
};

export const updateTrial = async (
  fieldId: string,
  trialId: string,
  data: Partial<{
    name: string;
    shape: TTrialShape;
    pointA: { lng: number; lat: number };
    pointB: { lng: number; lat: number };
    widthMeters: number;
    geometry: geojson.Polygon;
  }>
): Promise<TFieldTrial> => {
  return api.patch<TFieldTrial>(`/api/v1/fields/${fieldId}/trials/${trialId}`, data);
};

export const getAllTrials = async (
  seasonId: string
): Promise<TFieldTrial[]> => {
  const res = await api.get<{ trials: TFieldTrial[] }>(
    "/api/v1/fields/all-trials",
    { seasonId }
  );
  return res.trials;
};

export const getFieldsWithTrials = async (
  seasonId?: string
): Promise<TFieldListItem[]> => {
  const res = await api.get<{ fields: TFieldListItem[] }>(
    "/api/v1/fields/with-trials",
    { seasonId }
  );
  return res.fields;
};

export const deleteTrial = async (
  fieldId: string,
  trialId: string
): Promise<void> => {
  await api.delete(`/api/v1/fields/${fieldId}/trials/${trialId}`);
};

export const getTrialsYieldSummary = async (
  fieldId: string,
  seasonId: string,
  units?: { weight?: string; area?: string; poundsPerBushel?: number; poundsPerBag?: number }
): Promise<{ trials: TTrialWithYield[]; unit: string }> => {
  return api.get<{ trials: TTrialWithYield[]; unit: string }>(
    `/api/v1/fields/${fieldId}/trials/yield-summary`,
    {
      seasonId,
      weight: units?.weight,
      area: units?.area,
      poundsPerBushel: units?.poundsPerBushel,
      poundsPerBag: units?.poundsPerBag,
    }
  );
};

export const createSeason = async (
  from: Date,
  to: Date,
  copyFromSeasonId?: string
): Promise<string> => {
  const body = await api.post<{ id: string }>(
    "/api/v1/seasons",
    {
      name: `${from.getFullYear()}-${to.getFullYear()}`,
      start: from.toISOString(),
      end: to.toISOString(),
      isCurrent: true,
      copyFromSeasonId: copyFromSeasonId || undefined,
    },
    // Both create-season dialogs toast the failure themselves.
    { silent: true }
  );
  return body.id;
};

export const updateSeason = async (
  id: string,
  data: { from?: Date; to?: Date }
): Promise<void> => {
  const body: Record<string, unknown> = {};
  if (data.from) body.start = data.from.toISOString();
  if (data.to) body.end = data.to.toISOString();
  if (data.from && data.to) {
    body.name = `${data.from.getFullYear()}-${data.to.getFullYear()}`;
  }
  await api.patch(`/api/v1/seasons/${id}`, body);
};

export const deleteSeason = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/seasons/${id}`);
};
